/**
 * @fileoverview Type-safe API client for the Aswan Failure Analysis Platform.
 *
 * This module provides a typed API client built on openapi-fetch that automatically
 * handles authentication, error handling, and provides full TypeScript type inference
 * based on the OpenAPI specification.
 *
 * @example
 * ```typescript
 * import { api, setAuthToken } from './api/client';
 *
 * // Login and store token
 * const { data } = await api.POST('/api/auth/login', {
 *   body: { email: 'user@example.com', password: 'password' }
 * });
 * setAuthToken(data.token);
 *
 * // Make authenticated requests
 * const cases = await api.GET('/api/cases', {
 *   params: { query: { page: 1, pageSize: 20 } }
 * });
 * ```
 */

import createClient from 'openapi-fetch';
import type { paths, components } from './types';

/**
 * Storage key for the JWT authentication token.
 */
const AUTH_TOKEN_KEY = 'aswan_auth_token';

/**
 * API base URL. Defaults to the development server if VITE_API_URL is not set.
 */
export const API_BASE_URL = import.meta.env['VITE_API_URL'] || 'http://localhost:8001';

/**
 * Retrieves the stored authentication token from localStorage.
 *
 * @returns The stored JWT token, or null if not set
 */
export function getAuthToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

/**
 * Stores the authentication token in localStorage.
 * Call this after a successful login to persist the token.
 *
 * @param token - The JWT token to store
 */
export function setAuthToken(token: string): void {
  localStorage.setItem(AUTH_TOKEN_KEY, token);
}

/**
 * Removes the stored authentication token from localStorage.
 * Call this on logout to clear the authentication state.
 */
export function clearAuthToken(): void {
  localStorage.removeItem(AUTH_TOKEN_KEY);
}

/**
 * Checks if a valid authentication token is stored.
 *
 * @returns True if a token exists, false otherwise
 */
export function isAuthenticated(): boolean {
  return getAuthToken() !== null;
}

/**
 * Custom error class for API errors.
 * Provides structured access to error details from the API response.
 */
export class ApiError extends Error {
  /** HTTP status code */
  public readonly status: number;
  /** Machine-readable error code from the API */
  public readonly code: string;
  /** Request ID for tracing/debugging */
  public readonly requestId: string;
  /** Additional error details */
  public readonly details?: Record<string, unknown>;

  /**
   * Creates a new ApiError instance.
   *
   * @param message - Human-readable error message
   * @param status - HTTP status code
   * @param code - Machine-readable error code
   * @param requestId - Request ID for tracing
   * @param details - Additional error context
   */
  constructor(
    message: string,
    status: number,
    code: string,
    requestId: string,
    details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.requestId = requestId;
    // Only assign details if it's defined (for exactOptionalPropertyTypes compatibility)
    if (details !== undefined) {
      this.details = details;
    }
  }
}

/**
 * Creates the typed API client with authentication middleware.
 * The client automatically attaches the Bearer token to all requests
 * when an auth token is stored.
 *
 * @returns Configured openapi-fetch client instance
 */
function createApiClient() {
  const client = createClient<paths>({
    baseUrl: API_BASE_URL,
  });

  // Add request interceptor for authentication
  client.use({
    onRequest({ request }) {
      const token = getAuthToken();
      if (token) {
        request.headers.set('Authorization', `Bearer ${token}`);
      }
      return request;
    },
    async onResponse({ response }) {
      // Handle 401 responses by clearing the token
      if (response.status === 401 && getAuthToken()) {
        // Token is invalid or expired - clear it
        // The app should redirect to login when it detects no token
        clearAuthToken();
      }
      return response;
    },
  });

  return client;
}

/**
 * The main API client instance.
 *
 * Provides type-safe access to all API endpoints with full TypeScript inference.
 * The client handles:
 * - Automatic authentication header injection
 * - Automatic token clearing on 401 responses
 * - Full request/response type inference from OpenAPI spec
 *
 * @example
 * ```typescript
 * // GET request with query parameters
 * const { data, error } = await api.GET('/api/cases', {
 *   params: { query: { status: 'open', page: 1 } }
 * });
 *
 * // POST request with body
 * const { data, error } = await api.POST('/api/cases', {
 *   body: { vin: '...', oem: 'Caterpillar', ... }
 * });
 *
 * // Request with path parameters
 * const { data, error } = await api.GET('/api/cases/{id}', {
 *   params: { path: { id: 'case-uuid' } }
 * });
 * ```
 */
export const api = createApiClient();

// Re-export schema types for convenience
export type { paths, components };

/** Common schema types for use throughout the application */
export type Case = components['schemas']['Case'];
export type CaseStatus = components['schemas']['CaseStatus'];
export type CreateCaseRequest = components['schemas']['CreateCaseRequest'];
export type CaseListResponse = components['schemas']['CaseListResponse'];

export type Evidence = components['schemas']['Evidence'];
export type EvidenceType = components['schemas']['EvidenceType'];
export type EvidenceListResponse = components['schemas']['EvidenceListResponse'];

export type EvidenceRequest = components['schemas']['EvidenceRequest'];
export type EvidenceRequestStatus = components['schemas']['EvidenceRequestStatus'];
export type CreateEvidenceRequestBody = components['schemas']['CreateEvidenceRequestBody'];

export type Hypothesis = components['schemas']['Hypothesis'];
export type HypothesisStatus = components['schemas']['HypothesisStatus'];
export type HypothesisInput = components['schemas']['HypothesisInput'];
export type HypothesesListResponse = components['schemas']['HypothesesListResponse'];

export type ReportSnapshot = components['schemas']['ReportSnapshot'];
export type ReportSnapshotStatus = components['schemas']['ReportSnapshotStatus'];
export type ReportContent = components['schemas']['ReportContent'];
export type ReportsListResponse = components['schemas']['ReportsListResponse'];
export type ApprovalDecision = components['schemas']['ApprovalDecision'];

export type Message = components['schemas']['Message'];
export type CreateMessageRequest = components['schemas']['CreateMessageRequest'];
export type MessagesListResponse = components['schemas']['MessagesListResponse'];

export type CaseEvent = components['schemas']['CaseEvent'];
export type ActorType = components['schemas']['ActorType'];
export type EventsListResponse = components['schemas']['EventsListResponse'];

export type UserInfo = components['schemas']['UserInfo'];
export type UserRole = components['schemas']['UserRole'];
export type LoginRequest = components['schemas']['LoginRequest'];
export type LoginResponse = components['schemas']['LoginResponse'];

export type UserListItem = components['schemas']['UserListItem'];
export type UserListResponse = components['schemas']['UserListResponse'];
export type CreateUserRequest = components['schemas']['CreateUserRequest'];
export type CreateUserResponse = components['schemas']['CreateUserResponse'];
export type UpdateUserRoleRequest = components['schemas']['UpdateUserRoleRequest'];

export type TenantSettings = components['schemas']['TenantSettings'];
export type AgentType = components['schemas']['AgentType'];
export type TriggerAgentRequest = components['schemas']['TriggerAgentRequest'];
export type TriggerAgentResponse = components['schemas']['TriggerAgentResponse'];

export type CaseStudyListItem = components['schemas']['CaseStudyListItem'];
export type CaseStudyListResponse = components['schemas']['CaseStudyListResponse'];
export type CaseStudyDetail = components['schemas']['CaseStudyDetail'];
export type SymptomPattern = components['schemas']['SymptomPattern'];
export type EvidencePattern = components['schemas']['EvidencePattern'];

export type ErrorResponse = components['schemas']['Error'];

/**
 * External review status (pending or completed).
 * Note: This type is defined locally because the external review feature
 * was added after the initial OpenAPI types were generated.
 */
export type ExternalReviewStatus = 'pending' | 'completed';
